import React from 'react';
import { UploadFileStatus } from '@progress/kendo-react-upload';
import { useGetDocumentMetadata } from '@cs-cms/state-management';
import {
  CpsDocumentMetadata,
  FILE_FIELD,
  GetDocumentMetadataRequest,
  CPS_CONTENT_TYPE_FIELD_TYPE,
  CONTENT_TYPE_FIELD
} from '@cs-cms/types';

interface UseDocumentFormInitParams {
  indexId: number;
  isNewDocument: boolean;
  isRetry: boolean;
  fileUploadRequired: boolean;
  existingDocumentId?: string;
  existingDocumentCtName?: string;
  prefillData?: {
    contentType?: string;
    metadata?: CpsDocumentMetadata;
  };
  contentTypeFields: any[];
  perFileFields: any[];
  selectedContentTypeName?: string;
}

interface FormValues extends CpsDocumentMetadata {
  [FILE_FIELD]?: any[];
  [key: string]: any;
}

export const useDocumentFormInit = ({
  indexId,
  isNewDocument,
  isRetry,
  fileUploadRequired,
  existingDocumentId,
  existingDocumentCtName,
  prefillData,
  contentTypeFields,
  perFileFields,
  selectedContentTypeName
}: UseDocumentFormInitParams) => {
  const [initialFormValues, setInitialFormValues] = React.useState<FormValues>({});
  const [documentETag, setDocumentETag] = React.useState<string | undefined>();
  const [formKey, setFormKey] = React.useState(0);

  const documentMetadataRequest: GetDocumentMetadataRequest | undefined =
    isNewDocument || !existingDocumentId || !existingDocumentCtName
      ? undefined
      : { indexId, ctName: existingDocumentCtName, documentId: existingDocumentId };

  const { data: existingDocData, isLoading: isLoadingExistingDoc } = useGetDocumentMetadata(documentMetadataRequest);

  const convertDateFields = (values: CpsDocumentMetadata): CpsDocumentMetadata => {
    const converted = { ...values };

    for (const [key, value] of Object.entries(converted)) {
      if (value === null) {
        converted[key] = undefined;
      }

      if (typeof value === 'string') {
        const foundCtField = contentTypeFields.find(ctField => ctField.name === key);
        if (
          foundCtField?.type === CPS_CONTENT_TYPE_FIELD_TYPE.DATE ||
          foundCtField?.type === CPS_CONTENT_TYPE_FIELD_TYPE.DATETIME
        ) {
          converted[key] = new Date(value);
        }
      }
    }

    return converted;
  };

  const extractPerFileMetadata = (values: CpsDocumentMetadata): Record<string, any> => {
    const perFileMetadata: Record<string, any> = {};
    perFileFields.forEach(field => {
      if (values[field.name] !== undefined) {
        perFileMetadata[field.name] = values[field.name];
      }
    });
    return perFileMetadata;
  };

  const applyDefaultValues = (values: FormValues): FormValues => {
    const withDefaults = { ...values };

    contentTypeFields.forEach(field => {
      if (
        (withDefaults[field.name] === undefined || withDefaults[field.name] === null) &&
        field.defaultValue !== null &&
        field.defaultValue !== undefined
      ) {
        withDefaults[field.name] = field.defaultValue;
      }
    });

    return withDefaults;
  };

  React.useEffect(() => {
    if (isNewDocument || isRetry) {
      if (prefillData?.metadata) {
        const ctNameObj = prefillData.contentType
          ? { [CONTENT_TYPE_FIELD]: { id: prefillData.contentType, name: prefillData.contentType } }
          : {};
        const valuesWithDefaults = applyDefaultValues({
          ...prefillData.metadata,
          ...ctNameObj
        });
        setInitialFormValues(valuesWithDefaults);
        setFormKey(prevKey => prevKey + 1);
      } else {
        const baseValues = selectedContentTypeName
          ? { [CONTENT_TYPE_FIELD]: { id: selectedContentTypeName, name: selectedContentTypeName } }
          : {};
        const valuesWithDefaults = applyDefaultValues(baseValues);
        setInitialFormValues(valuesWithDefaults);
        setFormKey(prevKey => prevKey + 1);
      }

      return;
    }

    if (existingDocData) {
      const values = convertDateFields(existingDocData.data.metadata);
      setDocumentETag(existingDocData.headers.etag);

      const perFileMetadata = extractPerFileMetadata(values);

      const fileField =
        isRetry && fileUploadRequired
          ? []
          : [
              {
                name: values.Cps_FileName as string,
                size: 0,
                extension: values.Cps_Extension as string,
                uid: values.Cps_DocId as string,
                status: UploadFileStatus.Initial,
                progress: 100,
                documentId: values.Cps_DocId as string,
                perFileMetadata
              }
            ];

      setInitialFormValues({
        ...values,
        [FILE_FIELD]: fileField,
        [CONTENT_TYPE_FIELD]: { name: values[CONTENT_TYPE_FIELD] }
      });
      setFormKey(prevKey => prevKey + 1);
    }
  }, [
    isNewDocument,
    isRetry,
    prefillData,
    existingDocData,
    fileUploadRequired,
    convertDateFields,
    extractPerFileMetadata,
    applyDefaultValues,
    selectedContentTypeName
  ]);

  const isLoading = !isNewDocument && (isLoadingExistingDoc || Object.keys(initialFormValues).length === 0);

  return {
    initialFormValues,
    documentETag,
    formKey,
    isLoading,
    existingDocumentData: existingDocData
  };
};
